<script setup lang="ts">
// Override for markstream's `reference` node ([1], [2] inline citations).
// Registered in each app's main.ts via the VueRendererMarkdown `components` map.
//
// Resolves [n] against the message's source list (injected from CitationProvider,
// derived from the bubble's tool results - see lib/citations.ts). When a source
// exists the chip is interactive: hover/focus pops a preview card (title, host,
// snippet); clicking a web source opens it, a workspace/memory source scrolls to
// its row in the fold below. When none exists - a stray [1] in prose, an array
// index like list[0], or a citation past the end of the list - it degrades to a
// neutral, non-interactive marker so it never reads as a broken button.
//
// The hover-preview is Reka UI's HoverCard: it owns the open/close hover-intent
// delays, Floating-UI positioning (collision-aware flip), and the body portal -
// replacing the hand-rolled getBoundingClientRect + scroll/resize listeners this
// used to carry. Icons are injected (useIcons) so each app uses its own set.
import { computed, inject, ref } from 'vue';
import type { Component } from 'vue';
import { HoverCardRoot, HoverCardTrigger, HoverCardPortal, HoverCardContent } from 'reka-ui';
import { useIcons } from '../icons';
import { CITATION_KEY, type Citation, type CitationKind } from '../lib/citations';

defineOptions({ inheritAttrs: false });

const props = defineProps<{ node: { type: string; id: string; raw?: string } }>();

const EMPTY = computed<Citation[]>(() => []);
const citations = inject(CITATION_KEY, EMPTY);

const idx = computed(() => {
  const n = Number(props.node.id);
  return Number.isInteger(n) && n > 0 ? n : 0;
});
const cite = computed<Citation | undefined>(() => (idx.value ? citations.value[idx.value - 1] : undefined));

const icons = useIcons();
const KIND_ICON: Record<CitationKind, Component> = { web: icons.globe, workspace: icons.chat, memory: icons.brain };
const KIND_LABEL: Record<CitationKind, string> = { web: 'source', workspace: 'workspace message', memory: 'memory' };
const ariaLabel = computed(() => {
  const c = cite.value;
  if (!c) return undefined;
  return `Citation ${props.node.id}: ${KIND_LABEL[c.kind]}${c.title ? ` - ${c.title}` : ''}`;
});

// Controlled open so we can dismiss the card the moment the chip is activated
// (otherwise it lingers while the page scrolls/navigates). Reka still drives
// open/close on hover + focus via the same v-model.
const open = ref(false);
const chipEl = ref<HTMLElement | null>(null);

function onClick(): void {
  const c = cite.value;
  if (!c) return;
  open.value = false;
  if (c.url) { window.open(c.url, '_blank', 'noopener,noreferrer'); return; }
  revealRow(); // workspace/memory have no URL - reveal the row in the fold below
}

// Find this citation's row in the source fold under the same message, open the
// fold, scroll to it, and flash it. The row carries data-cite="<flattened index>"
// (tagged by the viewparts). We walk up from the chip to the nearest ancestor
// whose subtree holds that row - so we never reach into a sibling message.
function revealRow(): void {
  const chip = chipEl.value;
  if (!chip) return;
  const sel = `[data-cite="${idx.value}"]`;
  let el: HTMLElement | null = chip.parentElement;
  let row: HTMLElement | null = null;
  while (el && !row) {
    row = el.querySelector<HTMLElement>(sel);
    el = el.parentElement;
  }
  if (!row) return;
  const target = row;
  const details = target.closest('details');
  if (details) details.open = true;
  requestAnimationFrame(() => {
    target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    target.classList.remove('cite-flash');
    void target.offsetWidth; // restart the flash animation
    target.classList.add('cite-flash');
  });
}
</script>

<template>
  <!-- No source for this index: neutral, non-interactive marker. -->
  <sup v-if="!cite" class="hqcite hqcite--bare">{{ node.id }}</sup>

  <!-- Resolvable citation: interactive chip + hover/focus preview card. -->
  <HoverCardRoot v-else v-model:open="open" :open-delay="110" :close-delay="160">
    <HoverCardTrigger as-child>
      <sup
        ref="chipEl"
        class="hqcite hqcite--live"
        :class="`hqcite--${cite.kind}`"
        :role="cite.url ? 'link' : 'button'"
        tabindex="0"
        :aria-label="ariaLabel"
        @keydown.enter="onClick"
        @click="onClick"
      >{{ node.id }}</sup>
    </HoverCardTrigger>
    <HoverCardPortal>
      <HoverCardContent
        class="hqcite-card"
        side="top"
        :side-offset="6"
        :collision-padding="8"
      >
        <div class="hqcite-card__head">
          <component :is="KIND_ICON[cite.kind]" :size="13" class="hqcite-card__icon" />
          <span class="hqcite-card__host">{{ cite.host || cite.meta || KIND_LABEL[cite.kind] }}</span>
          <component :is="icons.externalLink" v-if="cite.url" :size="12" class="hqcite-card__out" />
        </div>
        <div v-if="cite.title && cite.title !== cite.host" class="hqcite-card__title">{{ cite.title }}</div>
        <p v-if="cite.snippet" class="hqcite-card__snippet">{{ cite.snippet }}</p>
      </HoverCardContent>
    </HoverCardPortal>
  </HoverCardRoot>
</template>

<style scoped>
/* Inline chip - small raised numeric marker. Tokens flip with the theme. */
.hqcite {
  font-size: 0.72em;
  line-height: 0;
  vertical-align: super;
  padding: 0.1em 0.4em;
  margin: 0 0.1em;
  border-radius: 5px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.hqcite--bare {
  background: var(--surface-sunken);
  color: var(--ink-muted);
}
.hqcite--live {
  background: var(--surface-sunken);
  color: var(--accent-default);
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
  text-decoration: none;
}
.hqcite--live:hover,
.hqcite--live:focus-visible {
  background: var(--accent-default);
  color: var(--surface-canvas);
  outline: none;
}

/* (Preview-card styles live in the unscoped block below: Reka portals the card
   to <body>, so a scoped hash wouldn't reach it.) */
</style>

<style>
/* Preview card. Reka positions it (Floating UI) and portals it to <body>, so we
   only style it - no position/transform here. */
.hqcite-card {
  z-index: 60;
  box-sizing: border-box;
  width: max-content;
  max-width: min(320px, calc(100vw - 16px));
  padding: var(--space-2, 8px) var(--space-3, 12px);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md, 10px);
  background: var(--surface-panel);
  box-shadow: 0 8px 28px -6px rgba(0, 0, 0, 0.28);
  font-size: var(--text-xs, 12px);
  color: var(--ink-muted);
}
.hqcite-card[data-state="open"] { animation: hqcite-in 0.12s ease; }
@keyframes hqcite-in {
  from { opacity: 0; }
}
.hqcite-card__head {
  display: flex;
  align-items: center;
  gap: var(--space-1, 4px);
  color: var(--ink-subtle);
}
.hqcite-card__icon { flex: 0 0 auto; }
.hqcite-card__host {
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hqcite-card__out { flex: 0 0 auto; }
.hqcite-card__title {
  margin-top: 2px;
  font-size: var(--text-sm, 13px);
  font-weight: 500;
  line-height: 1.35;
  color: var(--ink-display);
}
.hqcite-card__snippet {
  margin: var(--space-1, 4px) 0 0;
  line-height: 1.5;
  color: var(--ink-muted);
  display: -webkit-box;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
@media (prefers-reduced-motion: reduce) {
  .hqcite-card[data-state="open"] { animation: none; }
}
</style>
